/**
 * Google Business → Reviews.
 *
 * Read the reviews for a connected location, and answer them.
 *
 * THE ONE THING THAT MATTERS HERE: Google applies PUT semantics to owner replies
 * and keeps no history. Replying to a review that already has a reply destroys
 * the previous one permanently — including one a colleague wrote by hand in
 * Google's own console, which this screen cannot see happening. So replacing is
 * always confirmed first, and the existing reply is shown in full next to the
 * box that is about to overwrite it.
 */
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, MessageSquare, Sparkles, Star, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { Select } from '@/components/ui/Select';
import {
  PAGE_SIZE,
  REVIEW_SORT_OPTIONS,
  type ReviewFilters,
} from '@/pages/marketing/google-business/reviewFilters';
import {
  deleteGoogleReviewReply,
  draftGoogleReviewReply,
  listGoogleReviews,
  replyToGoogleReview,
  type GoogleReview,
  type GoogleReviewsPage,
  type ReviewSort,
} from '@/services/googleBusiness.service';


function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={`h-3.5 w-3.5 ${n <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
        />
      ))}
    </span>
  );
}

function when(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

function ReviewCard({
  review,
  locationId,
  onChanged,
}: {
  review: GoogleReview;
  locationId: string;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(review.reply?.comment ?? '');
  const [saving, setSaving] = useState(false);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [drafting, setDrafting] = useState(false);
  // Shown only once a draft has actually been generated — the rules are about
  // what the AI wrote, so they appear with it rather than sitting there always.
  const [notice, setNotice] = useState<string | null>(null);

  const generate = async () => {
    setDrafting(true);
    try {
      const res = await draftGoogleReviewReply(review.rating, review.comment);
      // Replaces the box contents, which is why it is only offered while the
      // reply editor is open and the user can see what it overwrote.
      setDraft(res.draft);
      setNotice(res.notice);
    } catch (e) {
      toast.error(getErrorMessage(e, 'A suggested reply could not be generated.'));
    } finally {
      setDrafting(false);
    }
  };

  const post = async () => {
    setSaving(true);
    try {
      await replyToGoogleReview(locationId, review.id, draft.trim());
      toast.success('Reply posted to Google');
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(getErrorMessage(e, 'The reply could not be posted.'));
    } finally {
      setSaving(false);
      setConfirmReplace(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await deleteGoogleReviewReply(locationId, review.id);
      toast.success('Reply removed');
      onChanged();
    } catch (e) {
      toast.error(getErrorMessage(e, 'The reply could not be removed.'));
    } finally {
      setSaving(false);
      setConfirmRemove(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-slate-900">
              {review.reviewer.isAnonymous ? 'Anonymous' : review.reviewer.displayName || 'Someone'}
            </span>
            <Stars rating={review.rating} />
            <span className="text-xs text-slate-500">{when(review.createTime)}</span>
          </div>
          {review.comment ? (
            <p className="mt-2 whitespace-pre-wrap text-sm text-slate-700">{review.comment}</p>
          ) : (
            <p className="mt-2 text-sm italic text-slate-500">Rating only — no written review.</p>
          )}
        </div>
        {review.reply ? (
          <Badge variant="success">Replied</Badge>
        ) : (
          <Badge variant="warning">Needs a reply</Badge>
        )}
      </div>

      {review.reply && (
        <div className="mt-3 rounded-lg border-l-2 border-slate-300 bg-slate-50 px-3 py-2">
          <p className="text-xs font-medium text-slate-600">
            Your reply · {when(review.reply.updateTime)}
          </p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{review.reply.comment}</p>
        </div>
      )}

      {open ? (
        <div className="mt-3 space-y-2">
          <Textarea
            rows={3}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Write a reply…"
            autoFocus
          />
          {/* Said before the click, not after: there is no undo on Google. */}
          {review.reply && (
            <p className="text-xs text-amber-700">
              This replaces your existing reply. Google keeps no history of the old one.
            </p>
          )}

          {/* Travels with the draft rather than living permanently on the page,
              so the rules are read at the moment they apply. */}
          {notice && (
            <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-xs text-slate-600">
              {notice}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              disabled={saving || draft.trim() === ''}
              onClick={() => (review.reply ? setConfirmReplace(true) : void post())}
            >
              {review.reply ? 'Replace reply' : 'Post reply'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={saving || drafting}
              leftIcon={<Sparkles className="h-3.5 w-3.5" />}
              onClick={() => void generate()}
            >
              {drafting ? 'Writing…' : draft.trim() ? 'Suggest another' : 'Suggest a reply'}
            </Button>
            <Button variant="secondary" size="sm" disabled={saving} onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<MessageSquare className="h-3.5 w-3.5" />}
            onClick={() => {
              setDraft(review.reply?.comment ?? '');
              setOpen(true);
            }}
          >
            {review.reply ? 'Edit reply' : 'Reply'}
          </Button>
          {review.reply && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => setConfirmRemove(true)}
            >
              Remove reply
            </Button>
          )}
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmReplace}
        onClose={() => setConfirmReplace(false)}
        onConfirm={post}
        title="Replace your reply?"
        message="Google keeps no history, so the reply currently showing will be gone for good — including any edit made directly in Google Business Profile."
        confirmLabel="Replace it"
      />
      <ConfirmDialog
        isOpen={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={remove}
        title="Remove your reply?"
        message="The review stays on Google; only your reply is deleted. This cannot be undone."
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}

/**
 * The results half of the Reviews screen.
 *
 * Filter STATE is owned by the page (see reviewFilters.ts) because the filter
 * rail is a page-level panel beside the main navigation, the same position
 * Patients uses — the rail and these results are siblings, not parent and child.
 */
export function GoogleReviewsTab({
  locationId,
  filters,
}: {
  locationId: string;
  filters: ReviewFilters;
}) {
  const { query, sort, setSort, page, setPage, activeFilterCount, clearAll } = filters;

  const [result, setResult] = useState<GoogleReviewsPage | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setResult(await listGoogleReviews(locationId, query));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Your Google reviews could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [locationId, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const total = result?.total ?? 0;
  const totalPages = result?.totalPages ?? 1;
  const first = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const last = Math.min(page * PAGE_SIZE, total);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white px-4 py-3">
        <div>
          <p className="text-xs text-slate-500">Average rating</p>
          <p className="text-xl font-semibold text-slate-900">
            {(result?.averageRating ?? 0).toFixed(1)}
          </p>
        </div>
        <div className="h-8 w-px bg-slate-200" />
        <div>
          <p className="text-xs text-slate-500">Total reviews</p>
          <p className="text-xl font-semibold text-slate-900">{result?.totalReviewCount ?? 0}</p>
        </div>
      </div>

      {result?.truncated && (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          This listing has more reviews than can be loaded at once, so filters and counts cover the
          most recent ones rather than the whole history.
        </p>
      )}

      <div className="rounded-xl border border-slate-200 bg-white">
        <div className="min-w-0 space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-600">
              {total === 0
                ? 'No reviews match these filters'
                : `Showing ${first}\u2013${last} of ${total} review${total === 1 ? '' : 's'}`}
            </p>

            <div className="flex items-center gap-2">
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearAll}>
                  Clear filters
                </Button>
              )}
              <div className="w-44">
                <Select
                  options={REVIEW_SORT_OPTIONS}
                  value={sort}
                  onChange={(v) => setSort(v as ReviewSort)}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <SpinnerOverlay label="Loading reviews\u2026" />
          ) : total === 0 ? (
            <EmptyState
              icon={<Star className="h-10 w-10 text-slate-300" />}
              title={activeFilterCount > 0 ? 'No reviews match these filters' : 'No reviews yet'}
              description={
                activeFilterCount > 0
                  ? 'Try widening the rating, date or reply-status filters.'
                  : "Reviews left on this location's Google Business Profile will appear here."
              }
              action={
                activeFilterCount > 0
                  ? { label: 'Clear filters', variant: 'secondary', onClick: clearAll }
                  : undefined
              }
            />
          ) : (
            <>
              {result?.reviews.map((r) => (
                <ReviewCard key={r.id} review={r} locationId={locationId} onChanged={load} />
              ))}

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-1 pt-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    leftIcon={<ChevronLeft className="h-4 w-4" />}
                  >
                    Previous
                  </Button>

                  {pageNumbers(page, totalPages).map((n, i) =>
                    n === null ? (
                      <span key={`gap-${i}`} className="px-1.5 text-sm text-slate-400">
                        &hellip;
                      </span>
                    ) : (
                      <Button
                        key={n}
                        variant={n === page ? 'primary' : 'ghost'}
                        size="sm"
                        onClick={() => setPage(n)}
                        aria-current={n === page ? 'page' : undefined}
                      >
                        {n}
                      </Button>
                    ),
                  )}

                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    rightIcon={<ChevronRight className="h-4 w-4" />}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Page numbers with gaps, so a listing with forty pages does not render forty
 * buttons. Always shows the first and last page plus a window around the current
 * one; `null` is a gap.
 */
function pageNumbers(current: number, total: number): Array<number | null> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const out: Array<number | null> = [1];
  const from = Math.max(2, current - 1);
  const to = Math.min(total - 1, current + 1);

  if (from > 2) out.push(null);
  for (let n = from; n <= to; n++) out.push(n);
  if (to < total - 1) out.push(null);
  out.push(total);

  return out;
}
